import React from 'react';
import { motion } from 'framer-motion';
import { ZapIcon, ServerIcon, GaugeIcon } from 'lucide-react';
import { AnimatedDataFlow } from './AnimatedDataFlow';
import { SystemNode } from './SystemNode';
const SCOPT_LOGO = 'https://cdn.magicpatterns.com/uploads/1pR8YyeP3DRb77Nee75a4m/Scopt_icon.png';
export function EnergySystemAnimation() {
  // All flows active continuously
  const allActive = true;
  // Standardized dimensions
  const nodeWidth = 140;
  const nodeHeight = 100;
  // Positions for perfect vertical/horizontal alignment
  const topY = 80; // Moved clouds down to create more space for title
  const bottomY = 440;
  // X positions - increased spacing between meter and charger
  const meterX = 250;
  const chargerX = 610;
  const nodes = {
    // Top row - clouds aligned directly above devices
    scoptCloud: {
      x: meterX,
      y: topY,
      w: nodeWidth,
      h: nodeHeight
    },
    cpmsCloud: {
      x: chargerX,
      y: topY,
      w: nodeWidth,
      h: nodeHeight
    },
    // Bottom row - devices
    meter: {
      x: meterX,
      y: bottomY,
      w: nodeWidth,
      h: nodeHeight
    },
    charger: {
      x: chargerX,
      y: bottomY,
      w: nodeWidth,
      h: nodeHeight
    }
  };
  // Calculate center point between the two tiles for title alignment
  const titleCenterX = (nodes.meter.x + nodes.meter.w / 2 + nodes.charger.x + nodes.charger.w / 2) / 2;
  return <div className="w-full h-screen bg-[#0a0e1a] flex items-center justify-center overflow-hidden" data-id="element-20">
      <div className="relative w-full max-w-6xl aspect-video" data-id="element-21">
        {/* Title - Centered and moved higher */}
        <motion.div className="absolute top-4 left-0 right-0 flex justify-center z-10" initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} data-id="element-22">
          <h1 className="text-4xl font-bold tracking-tight text-center" data-id="element-23">
            <span className="text-white/90" data-id="element-24">Works with any </span>
            <span className="bg-gradient-to-r from-emerald-400 via-cyan-400 to-violet-400 bg-clip-text text-transparent" style={{
            filter: 'drop-shadow(0 0 20px rgba(16, 185, 129, 0.4)) drop-shadow(0 0 40px rgba(139, 92, 246, 0.2))'
          }} data-id="element-25">
              OCPP-compliant
            </span>
            <span className="text-white/90" data-id="element-26"> charger</span>
          </h1>
        </motion.div>

        {/* Main SVG Canvas */}
        <svg viewBox="0 0 1000 600" className="w-full h-full" style={{
        fontFamily: 'Inter, system-ui, sans-serif'
      }} data-id="element-27">
          <defs data-id="element-28">
            {/* Background gradient */}
            <radialGradient id="bg-gradient" cx="50%" cy="50%" r="70%" data-id="element-29">
              <stop offset="0%" stopColor="#0f172a" data-id="element-30" />
              <stop offset="100%" stopColor="#0a0e1a" data-id="element-31" />
            </radialGradient>

            {/* Glow filters */}
            <filter id="glow-blue" x="-50%" y="-50%" width="200%" height="200%" data-id="element-32">
              <feGaussianBlur stdDeviation="6" result="coloredBlur" data-id="element-33" />
              <feMerge data-id="element-34">
                <feMergeNode in="coloredBlur" data-id="element-35" />
                <feMergeNode in="SourceGraphic" data-id="element-36" />
              </feMerge>
            </filter>
          </defs>

          {/* Background */}
          <rect width="100%" height="100%" fill="url(#bg-gradient)" data-id="element-37" />

          {/* Grid pattern */}
          <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse" data-id="element-38">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#1e293b" strokeWidth="0.5" opacity="0.3" data-id="element-39" />
          </pattern>
          <rect width="100%" height="100%" fill="url(#grid)" data-id="element-40" />

          {/* TOP ROW - CLOUDS */}

          {/* SCOPT CLOUD - aligned above meter */}
          <SystemNode x={nodes.scoptCloud.x} y={nodes.scoptCloud.y} width={nodes.scoptCloud.w} height={nodes.scoptCloud.h} label="Scopt Cloud" image={SCOPT_LOGO} color="#1e293b" glowColor="#10b981" active={allActive} delay={0.3} data-id="element-41" />

          {/* CPMS CLOUD - aligned above charger */}
          <SystemNode x={nodes.cpmsCloud.x} y={nodes.cpmsCloud.y} width={nodes.cpmsCloud.w} height={nodes.cpmsCloud.h} label="CPMS Cloud" color="#1e293b" glowColor="#8b5cf6" active={allActive} delay={0.4} icon={<ServerIcon size={28} data-id="element-43" />} data-id="element-42" />

          {/* Cloud to Cloud connection (horizontal) */}
          <AnimatedDataFlow startX={nodes.scoptCloud.x + nodes.scoptCloud.w} startY={nodes.scoptCloud.y + nodes.scoptCloud.h / 2} endX={nodes.cpmsCloud.x} endY={nodes.cpmsCloud.y + nodes.cpmsCloud.h / 2} color="#3b82f6" active={allActive} bidirectional={true} delay={0.3} duration={2.8} straight={true} flowType="data" data-id="element-44" />

          {/* API Label (Horizontal) */}
          <foreignObject x={(nodes.scoptCloud.x + nodes.scoptCloud.w + nodes.cpmsCloud.x) / 2 - 20} y={nodes.scoptCloud.y + nodes.scoptCloud.h / 2 - 12} width="40" height="24" data-id="element-45">
            <div className="flex items-center justify-center w-full h-full" data-id="element-46">
              <span className="px-2 py-0.5 text-[10px] font-bold text-white bg-black/60 backdrop-blur-sm rounded border border-white/10" data-id="element-47">
                API
              </span>
            </div>
          </foreignObject>

          {/* BOTTOM ROW - DEVICES */}

          {/* MAIN METER */}
          <SystemNode x={nodes.meter.x} y={nodes.meter.y} width={nodes.meter.w} height={nodes.meter.h} label="Main Meter" color="#1e293b" glowColor="#f59e0b" active={allActive} delay={0.8} icon={<GaugeIcon size={24} data-id="element-49" />} data-id="element-48" />

          {/* EV CHARGER */}
          <SystemNode x={nodes.charger.x} y={nodes.charger.y} width={nodes.charger.w} height={nodes.charger.h} label="EV Charger" color="#1e293b" glowColor="#8b5cf6" active={allActive} delay={1} icon={<ZapIcon size={28} data-id="element-51" />} data-id="element-50" />

          {/* VERTICAL DATA FLOWS */}

          {/* Main Meter → Scopt Cloud (vertical) */}
          <AnimatedDataFlow startX={nodes.meter.x + nodes.meter.w / 2} startY={nodes.meter.y} endX={nodes.scoptCloud.x + nodes.scoptCloud.w / 2} endY={nodes.scoptCloud.y + nodes.scoptCloud.h} color="#10b981" active={allActive} delay={0} duration={2.5} straight={true} flowType="data" data-id="element-52" />

          {/* Charger → CPMS Cloud (vertical) */}
          <AnimatedDataFlow startX={nodes.charger.x + nodes.charger.w / 2} startY={nodes.charger.y} endX={nodes.cpmsCloud.x + nodes.cpmsCloud.w / 2} endY={nodes.cpmsCloud.y + nodes.cpmsCloud.h} color="#8b5cf6" active={allActive} delay={0.5} duration={2.5} straight={true} flowType="data" data-id="element-53" />

          {/* OCPP Label (Vertical) */}
          <foreignObject x={nodes.charger.x + nodes.charger.w / 2 + 6} y={(nodes.cpmsCloud.y + nodes.cpmsCloud.h + nodes.charger.y) / 2 - 12} width="50" height="24" data-id="element-54">
            <div className="flex items-center justify-start w-full h-full" data-id="element-55">
              <span className="px-2 py-0.5 text-[10px] font-bold text-white bg-black/60 backdrop-blur-sm rounded border border-white/10" data-id="element-56">
                OCPP
              </span>
            </div>
          </foreignObject>
        </svg>
      </div>
    </div>;
}